// ═══════════════════════════════════════════════════════════════════════════
// Construcción del CFDI
//
// Extraído de timbrarFactura() en index.html v0.9.16 (línea 524), separando
// la parte PURA (armar el cuerpo) de la llamada HTTP a Facturapi.
//
// Esa separación es el punto: el cuerpo del CFDI se puede verificar contra los
// 22 comprobantes ya timbrados sin tocar la red ni gastar un timbre.
// ═══════════════════════════════════════════════════════════════════════════

import { SAT_PRODUCT_KEY_WINE, SAT_UNIT_KEY_BOTTLE, SAT_FORMA_PAGO, SAT_METODO_PAGO,
         SAT_USO_CFDI } from "./catalogos-sat.js";

// ── Mapeos de vocabulario propio → claves del SAT ──────────────────────────

export function mapFormaPago(method) {
  if (!method) return "03";
  const lower = method.toLowerCase();
  if (lower.includes("transferencia")) return "03";
  if (lower.includes("efectivo")) return "01";
  if (lower.includes("tarjeta") && lower.includes("créd")) return "04";
  if (lower.includes("tarjeta") && lower.includes("déb")) return "28";
  if (lower.includes("99") || lower.includes("definir")) return "99";
  return "03";
}

export function mapMetodoPago(timing) {
  if (!timing) return "PUE";
  const lower = timing.toLowerCase();
  if (lower.includes("parcialidades") || lower.includes("diferido") || lower.includes("ppd")) return "PPD";
  return "PUE";
}

export function mapUsoCfdi(use) {
  if (!use) return "G01";
  const lower = use.toLowerCase();
  if (lower.includes("adquisición") || lower.includes("mercancia")) return "G01";
  if (lower.includes("gastos") || lower.includes("general")) return "G03";
  if (lower.includes("sin efectos")) return "S01";
  return "G01";
}

// Lo que ya es clave del catálogo se queda; una leyenda heredada se traduce a
// su clave, y vacío sigue vacío ("el de siempre"). Los clientes importados del
// sistema anterior traían "Transferencia Bancaria" donde va "03": el editor la
// heredaba y la remisión salía con la leyenda repetida (migración 0066).
const clave = (catalogo, mapa) => (v) =>
  !v ? "" : catalogo.some(x => x.code === v) ? v : mapa(v);
export const claveFormaPago = clave(SAT_FORMA_PAGO, mapFormaPago);
export const claveMetodoPago = clave(SAT_METODO_PAGO, mapMetodoPago);
export const claveUsoCfdi = clave(SAT_USO_CFDI, mapUsoCfdi);

// ── Construcción del cuerpo ────────────────────────────────────────────────

/**
 * Arma el cuerpo que se envía a Facturapi. Función PURA: mismas entradas,
 * mismo resultado, sin efectos.
 *
 * ⚠ DEUDA CONOCIDA, conservada a propósito para no cambiar comportamiento:
 *
 *   1. `li.iepsRate || prod?.iepsRate || 26.5` — un producto con IEPS 0
 *      recibe 26.5 %, porque 0 es falsy. Hoy da igual (todo es alcohol), pero
 *      romperá con los servicios enológicos, de asesoría y de TI, que no
 *      causan IEPS. Se corrige al introducir product.type — no antes.
 *
 *   2. `SAT_PRODUCT_KEY_WINE` fijo para toda línea. En el modelo nuevo la
 *      clave viaja por producto.
 */
export function construirCfdi(invoiceData, products) {
  const cl = invoiceData.client;
  // El régimen de ESTA remisión manda sobre el del cliente, igual que ya
  // pasa con la forma de pago, el método y el uso de CFDI. Un cliente con
  // varios regímenes se factura con el que se eligió, no con el primero.
  const taxSystem = invoiceData.regimenFiscal || cl.taxSystem || "612";
  const fiscalZip = cl.domicilioFiscalZip || cl.cp || "00000";
  const discountPct = invoiceData.discount || 0;
  const ivaRate = invoiceData.ivaRate / 100;

  // Se excluyen las líneas de precio cero (botellas de cortesía).
  // ── Lo que se le declara al SAT es lo COBRABLE ───────────────────────
  //
  // Las botellas de cortesía se entregan y no se facturan. Antes eso salía
  // solo porque iban en un producto de precio 0 y este filtro las tiraba;
  // ahora van en el mismo renglón que las vendidas, así que hay que restarlas
  // a mano — y el resultado ante el SAT es EXACTAMENTE el mismo que hasta hoy,
  // que es lo que se quiso conservar (migración 0060).
  //
  // Un renglón enteramente de cortesía queda en cero y desaparece del
  // comprobante, igual que desaparecía antes.
  const items = invoiceData.lines.filter(li => {
    const price = li.unitPrice || (li.amount / li.qty);
    return price > 0 && (li.qty - (li.cortesia || 0)) > 0;
  }).map(li => {
    const prod = products.find(p => p.id === li.productId);
    const price = li.unitPrice || li.amount / li.qty;
    const lineIepsRate = li.iepsRate || prod?.iepsRate || 26.5;   // ← deuda (1)

    // Se declara lo COBRABLE, no lo entregado. Las de cortesía salen de la
    // bodega —el inventario las descuenta— y no van al comprobante.
    const cobrables = li.qty - (li.cortesia || 0);

    // El CFDI exige importes a 2 decimales.
    const lineSubtotal = Math.round(price * cobrables * 100) / 100;
    const lineDiscount = discountPct > 0
      ? Math.round(lineSubtotal * (discountPct / 100) * 100) / 100
      : undefined;

    return {
      quantity: cobrables,
      discount: lineDiscount,
      product: {
        description: prod?.name || li.productName || "Vino",
        product_key: SAT_PRODUCT_KEY_WINE,                        // ← deuda (2)
        unit_key: SAT_UNIT_KEY_BOTTLE,
        unit_name: "Botella",
        price: Math.round(price * 100) / 100,
        tax_included: false,
        taxes: [
          { type: "IEPS", rate: lineIepsRate / 100, factor: "Tasa" },
          { type: "IVA",  rate: ivaRate,            factor: "Tasa" },
        ],
      },
    };
  });

  return {
    customer: {
      legal_name: cl.legalName || cl.name,
      tax_id: cl.rfc,
      tax_system: taxSystem,
      email: cl.email || undefined,
      address: { zip: fiscalZip },
    },
    items,
    payment_form:   invoiceData.formaPago  || mapFormaPago(cl.paymentMethod),
    payment_method: invoiceData.metodoPago || mapMetodoPago(cl.paymentTiming),
    use:            invoiceData.usoCfdi    || mapUsoCfdi(cl.cfdiUse),
    folio_number:   invoiceData.folio,
    series: "VT",
  };
}

/**
 * Recalcula el total que resultará del cuerpo del CFDI, replicando cómo lo
 * computa Facturapi: por línea, base descontada, IEPS sobre esa base, IVA
 * sobre (base + IEPS).
 *
 * Sirve para comprobar ANTES de timbrar que el CFDI cuadrará con la remisión.
 * Hoy esa comprobación ocurre DESPUÉS, ya gastado el timbre (línea 597).
 */
export function totalEsperadoDelCfdi(body) {
  let subtotal = 0, ieps = 0, iva = 0;

  for (const item of body.items) {
    const base = item.product.price * item.quantity - (item.discount || 0);
    const tIeps = item.product.taxes.find(t => t.type === "IEPS");
    const tIva  = item.product.taxes.find(t => t.type === "IVA");
    const lineaIeps = tIeps ? base * tIeps.rate : 0;
    const lineaIva  = tIva  ? (base + lineaIeps) * tIva.rate : 0;
    subtotal += base;
    ieps += lineaIeps;
    iva  += lineaIva;
  }

  return { subtotal, ieps, iva, total: subtotal + ieps + iva };
}
